import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AuthService } from './auth.service';

export type UxPersona = 'developer' | 'operations';

const STORAGE_KEY = 'parseeasy_ux_persona';
const SKIPPED_KEY = 'parseeasy_ux_persona_skipped';

/**
 * Persists Developer vs Operations persona via user preferences + localStorage cache.
 * Used when ux_persona_first_run is enabled.
 */
@Injectable({
  providedIn: 'root'
})
export class UxPersonaService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly personaSubject = new BehaviorSubject<UxPersona | null>(this.readLocal());
  readonly persona$ = this.personaSubject.asObservable();

  constructor() {
    this.auth.currentUser$.subscribe(user => {
      if (user) {
        this.loadFromServer().subscribe();
      } else {
        this.personaSubject.next(this.readLocal());
      }
    });
  }

  get persona(): UxPersona | null {
    return this.personaSubject.value;
  }

  get hasChosenOrSkipped(): boolean {
    return this.persona !== null || localStorage.getItem(SKIPPED_KEY) === 'true';
  }

  /** Load persona from preferences API; falls back to localStorage. */
  loadFromServer(): Observable<UxPersona | null> {
    return this.http.get<Record<string, unknown>>(`${environment.apiUrl}/users/me/preferences`).pipe(
      map(prefs => this.normalize(prefs?.['ux_persona'])),
      tap(persona => {
        if (persona) {
          this.cacheLocal(persona);
          this.personaSubject.next(persona);
        }
      }),
      catchError(() => of(this.readLocal()))
    );
  }

  setPersona(persona: UxPersona): Observable<UxPersona> {
    this.cacheLocal(persona);
    localStorage.removeItem(SKIPPED_KEY);
    this.personaSubject.next(persona);

    return this.http
      .put<Record<string, unknown>>(`${environment.apiUrl}/users/me/preferences`, { ux_persona: persona })
      .pipe(
        map(() => persona),
        catchError(() => of(persona))
      );
  }

  skip(): void {
    localStorage.setItem(SKIPPED_KEY, 'true');
  }

  /** Reorder onboarding steps for the active persona (client-side emphasis). */
  orderSteps<T extends { id: string }>(steps: T[]): T[] {
    const persona = this.persona;
    if (!persona || !steps.length) {
      return steps;
    }

    const priority =
      persona === 'developer'
        ? ['parse', 'api_key', 'template', 'organization', 'profile']
        : ['parse', 'template', 'organization', 'api_key', 'profile'];

    return [...steps].sort((a, b) => {
      const ai = priority.indexOf(a.id);
      const bi = priority.indexOf(b.id);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }

  private normalize(value: unknown): UxPersona | null {
    if (value === 'developer' || value === 'operations') {
      return value;
    }
    return null;
  }

  private readLocal(): UxPersona | null {
    return this.normalize(localStorage.getItem(STORAGE_KEY));
  }

  private cacheLocal(persona: UxPersona): void {
    localStorage.setItem(STORAGE_KEY, persona);
  }
}
